import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Box, Tabs, Tab,
  TextField, InputAdornment, Paper, List, ListItem,
  ListItemButton, ListItemAvatar, Avatar, Card,
  CardContent, Grid, FormControl, InputLabel, Select,
  MenuItem, Chip, CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  PersonOutline as PersonOutlineIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Work,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import countryOptions from '../../constants/Countries';
import { useOffice } from '../../store/officeContext';
import { Navigate } from 'react-router-dom';

const ROLE_LABELS = {
  admin: 'Administrador',
  partner: 'Colaborador',
  client: 'Cliente',
  //procurator: 'Procurador',
  lawyer: 'Abogado' 
};

const InviteMemberModal = ({ 
  open, 
  onClose, 
  officeId, 
  officeColor,
  onMemberAdded 
}) => {
  // Estados principales
  const office = useOffice().office;
  const [modalTab, setModalTab] = useState(0);
  const [selectedRole, setSelectedRole] = useState('client');
  
  // Estados para usuarios existentes
  const [inviteSearchTerm, setInviteSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inviting, setInviting] = useState(false);

  const [jobPositions, setJobPositions] = useState([]);
  
  // Estados para nuevo usuario
  const [newUser, setNewUser] = useState({
    name: '',
    last_name: '',
    email: '',
    document_number: '',
    phone_number: '',
    country_code: '+34'
  });
  const [registering, setRegistering] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [emailExistsError, setEmailExistsError] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [usersInOffice, setUsersInOffice] = useState([]);

  React.useEffect(() => {
    loadJobPositions();
  }, []);

  const useDebounce = (callback, delay) => {
    const timeoutRef = React.useRef(null);

    return React.useCallback((...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }, [callback, delay]);
  };

  const debouncedEmailCheck = useDebounce((email) => {
    checkEmailExists(email);
  }, 800);

  const loadJobPositions = async () => {
    const { data, error } = await supabase
      .from("job_position")
      .select("*")
      .eq("office_id", office.id);

    if (!error) {
      setJobPositions(data || []);
    } else {
      console.error("Error loading job positions:", error.message);
    }
  };

  const darkenHex = (hex, amount = 0.1) => {
    let col = hex.startsWith('#') ? hex.slice(1) : hex;
    if (col.length === 3) {
      col = col.split('').map(c => c + c).join('');
    }

    const num = parseInt(col, 16);
    const r = Math.max(0, ((num >> 16) & 255) * (1 - amount));
    const g = Math.max(0, ((num >> 8) & 255) * (1 - amount));
    const b = Math.max(0, (num & 255) * (1 - amount));

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const handleSearch = async (value) => {
    setInviteSearchTerm(value);
    if (value.length < 3) {
      setSearchResults([]);
      setUsersInOffice([]);
      setShowDropdown(false);
      return;
    }

    // Obtener miembros existentes del despacho
    const { data: existingMembers } = await supabase
      .from('office_member')
      .select('member_id')
      .eq('office_id', officeId);

    const existingIds = existingMembers?.map(m => m.member_id) || [];

    // Buscar todos los usuarios que coincidan
    const { data: allResults, error } = await supabase
      .from('person')
      .select('id, full_name, email, document_number')
      .or(`email.ilike.%${value}%,document_number.ilike.%${value}%,full_name.ilike.%${value}%`)
      .limit(10);

    if (!error && allResults) {
      // Separar usuarios que ya están en el despacho de los que no
      const inOffice = allResults.filter(user => existingIds.includes(user.id));
      const notInOffice = allResults.filter(user => !existingIds.includes(user.id));
      
      setUsersInOffice(inOffice);
      setSearchResults(notInOffice);
      setShowDropdown(true);
    }
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setInviteSearchTerm(person.full_name);
    setShowDropdown(false);
  };

  const checkEmailExists = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      return false;
    }

    setCheckingEmail(true);
    
    try {
      const { data, error } = await supabase
        .from('person')
        .select('id, email, full_name')
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (!error && data) {
        setEmailExistsError(true);
        setValidationErrors(prev => ({
          ...prev,
          email: 'Este correo ya está registrado. Búscalo en "Usuarios existentes"'
        }));
        return true;
      } else {
        setEmailExistsError(false);
        setValidationErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        return false;
      }
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleInvite = async () => {
    if (!selectedPerson) return;
    setInviting(true);

    try {
      await supabase
        .from('office_member')
        .insert({
          office_id: officeId,
          member_id: selectedPerson.id,
          role: selectedRole
        });

      if (selectedRole === 'admin') {
        supabase.functions.invoke('sync-member-permissions', {
          body: { office_id: officeId }
        });
      }

      resetModal();
      onMemberAdded();
    } catch (error) {
      console.error('Error inviting member:', error);
    } finally {
      setInviting(false);
    }
  };

  const validateNewUser = () => {
    const errors = {};
    
    if (!newUser.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    if (!newUser.last_name.trim()) {
      errors.last_name = 'El apellido es requerido';
    }
    
    if (!newUser.email.trim()) {
      errors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(newUser.email)) {
      errors.email = 'El correo electrónico no es válido';
    }
    
    if (!newUser.document_number.trim()) {
      errors.document_number = 'El número de documento es requerido';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRegisterAndInvite = async () => {
    if (!validateNewUser()) return;

    // Verificar email una última vez antes de registrar
    const emailExists = await checkEmailExists(newUser.email);
    if (emailExists) {
      return;
    }
    
    setRegistering(true);

    const { data: { user }, error: authError } = await supabase.auth.getUser();
          
    if (authError) {
      console.error('Error getting authenticated user:', authError);
      return;
    }

    if (!user) {
      Navigate('/login');
      return;
    }
    
    try {
      // Crear el full_name combinando name y last_name
      const fullName = `${newUser.name.trim()}; ${newUser.last_name.trim()}`;

      const obj = {
        full_name: fullName,
        email: newUser.email,
        document_number: newUser.document_number,
        phone_number: newUser.phone_number || null,
        job_position: newUser.job_position,
        office_id: office.id,
        office_role: selectedRole,
        admin_id: user.id
      };

      //console.log(obj);

      const { data, error } = await supabase.functions.invoke('admin-user-register', {
        body: obj,
      })

      if (selectedRole === 'admin') {
        supabase.functions.invoke('sync-member-permissions', {
          body: { office_id: office.id }
        });
      }
          
      resetModal();
      onMemberAdded();
    } catch (error) {
      console.error('Error in register and invite:', error);
    } finally {
      setRegistering(false);
    }
  };

  const resetModal = () => {
    setSelectedPerson(null);
    setInviteSearchTerm('');
    setSearchResults([]);
    setUsersInOffice([]);
    setSelectedRole('client');
    setNewUser({
      name: '',
      last_name: '',
      email: '',
      document_number: '',
      phone_number: '',
      country_code: '+51'
    });
    setValidationErrors({});
    setEmailExistsError(false);
    setCheckingEmail(false);
    setInviting(false);
    setRegistering(false);
    setModalTab(0);
    setShowDropdown(false);
    onClose();
  };

  const handleClose = () => {
    resetModal();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: 2,
          height: '80vh',
          maxHeight: '150vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        Agregar Miembros al Despacho
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={modalTab} 
            onChange={(e, newValue) => setModalTab(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: officeColor,
              },
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: officeColor,
                },
                '&:hover': {
                  color: darkenHex(officeColor, 0.1),
                }
              }
            }}
          >
            <Tab label="Usuarios existentes" />
            <Tab label="Nuevo usuario" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {modalTab === 0 ? (
            // Usuarios existentes
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Buscar por correo electrónico o número de documento
              </Typography>
              
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  value={inviteSearchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Buscar personas..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: showDropdown && (
                      <InputAdornment position="end">
                        <KeyboardArrowDownIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.default',
                      borderRadius: 1
                    }
                  }}
                />

                {inviteSearchTerm.length > 0 && inviteSearchTerm.length < 3 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Escribe al menos 3 caracteres para buscar
                  </Typography>
                )}
                
                {/* Dropdown personalizado */}
                {showDropdown && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: 300,
                      overflow: 'auto',
                      mt: 1,
                      boxShadow: 3
                    }}
                  >
                    <List sx={{ p: 0 }}>
                      {/* Usuarios ya en el despacho */}
                      {usersInOffice.length > 0 && (
                        <>
                          <ListItem sx={{ bgcolor: 'warning.light', py: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="warning.dark">
                              YA EN EL DESPACHO
                            </Typography>
                          </ListItem>
                          {usersInOffice.map((person) => (
                            <ListItem
                              key={person.id}
                              disablePadding
                              sx={{ 
                                bgcolor: 'grey.50',
                                borderLeft: '3px solid',
                                borderColor: 'warning.main'
                              }}
                            >
                              <ListItemButton disabled>
                                <ListItemAvatar>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.400' }}>
                                    {person.full_name?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                </ListItemAvatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    {person.full_name.replace(';', '')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {person.email}
                                  </Typography>
                                  {person.document_number && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      DNI: {person.document_number}
                                    </Typography>
                                  )}
                                  <Chip 
                                    label="Ya es miembro" 
                                    size="small" 
                                    color="warning" 
                                    sx={{ mt: 0.5 }}
                                  />
                                </Box>
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </>
                      )}

                      {/* Usuarios disponibles para invitar */}
                      {searchResults.length > 0 && (
                        <>
                          {usersInOffice.length > 0 && (
                            <ListItem sx={{ bgcolor: 'success.light', py: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="success.dark">
                                DISPONIBLES PARA INVITAR
                              </Typography>
                            </ListItem>
                          )}
                          {searchResults.map((person) => (
                            <ListItem
                              key={person.id}
                              disablePadding
                              sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <ListItemButton onClick={() => handleSelectPerson(person)}>
                                <ListItemAvatar>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                    {person.full_name?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                </ListItemAvatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" sx={{ color: 'black', fontWeight: 500 }}>
                                    {person.full_name.replace(';', '')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'black' }}>
                                    {person.email}
                                  </Typography>
                                  {person.document_number && (
                                    <Typography variant="body2" sx={{ color: 'black' }}>
                                      DNI: {person.document_number}
                                    </Typography>
                                  )}
                                </Box>
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </>
                      )}

                      {/* No se encontró ningún usuario */}
                      {searchResults.length === 0 && usersInOffice.length === 0 && (
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => {
                            setModalTab(1);
                            setShowDropdown(false);
                          }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'grey.400' }}>
                                <PersonOutlineIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                No se encontró. ¿Registrar?
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Crear un nuevo usuario con estos datos.
                              </Typography>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              </Box>

              {/* Persona seleccionada */}
              {selectedPerson && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Persona seleccionada:
                  </Typography>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {selectedPerson.full_name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {selectedPerson.full_name.replace(';', '')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedPerson.email}
                          </Typography>
                          {selectedPerson.document_number && (
                            <Typography variant="body2" color="text.secondary">
                              DNI: {selectedPerson.document_number}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          ) : (
            // Nuevo usuario
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ingrese los datos del perfil y el rol asignado
              </Typography>

              {emailExistsError && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small"
                      onClick={() => {
                        setModalTab(0);
                        setInviteSearchTerm(newUser.email);
                        handleSearch(newUser.email);
                      }}
                    >
                      Buscar usuario
                    </Button>
                  }
                >
                  Este correo ya está registrado. Por favor, búscalo en la pestaña "Usuarios existentes".
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item size={{xs:12, sm:6}}>
                  <TextField
                    fullWidth
                    label="Nombres"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    error={!!validationErrors.name}
                    helperText={validationErrors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item size={{xs:12, sm:6}}>
                  <TextField
                    fullWidth
                    label="Apellidos"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                    error={!!validationErrors.last_name}
                    helperText={validationErrors.last_name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item size={{xs:12, sm:6}}>
                  <TextField
                    fullWidth
                    label="Número de documento"
                    value={newUser.document_number}
                    onChange={(e) => setNewUser({...newUser, document_number: e.target.value})}
                    error={!!validationErrors.document_number}
                    helperText={validationErrors.document_number}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item size={{xs:12, sm:6}}>
                  <Box sx={{ display: 'flex' }}>
                    <Select
                      value={newUser.country_code}
                      onChange={(e) =>
                        setNewUser({ ...newUser, country_code: e.target.value })
                      }
                      displayEmpty
                      sx={{ fontSize: "0.9rem", minWidth: '100px', mr: 1 }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <img
                              src={`https://flagcdn.com/w40/${country.iso2}.png`}
                              alt={country.name}
                              width={20}
                              style={{ borderRadius: "3px" }}
                            />
                            <span>{country.code}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      type='tel'
                      value={newUser.phone_number}
                      onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
                      error={!!validationErrors.phone_number}
                      helperText={validationErrors.phone_number}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid item size={{xs:12, sm:6}}>
                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => {
                      const email = e.target.value;
                      setNewUser({...newUser, email});
                      
                      // Limpiar error previo
                      if (emailExistsError) {
                        setEmailExistsError(false);
                        setValidationErrors(prev => {
                          const { email, ...rest } = prev;
                          return rest;
                        });
                      }
                      
                      // Verificar email si tiene formato válido
                      if (email && /\S+@\S+\.\S+/.test(email)) {
                        debouncedEmailCheck(email);
                      }
                    }}
                    error={!!validationErrors.email}
                    helperText={validationErrors.email || (checkingEmail ? 'Verificando...' : '')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                      endAdornment: checkingEmail && (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {selectedRole === 'partner' && (
                  <Grid item size={{xs:12, sm:6}}>
                    <FormControl fullWidth error={!!validationErrors.job_position}>
                      <InputLabel id="job-position-label">Puesto de trabajo</InputLabel>
                      <Select
                        labelId="job-position-label"
                        value={newUser.job_position || ""}
                        onChange={(e) =>
                          setNewUser({ ...newUser, job_position: e.target.value })
                        }
                        label="Puesto de trabajo"
                        startAdornment={
                          <InputAdornment position="start">
                            <Work />
                          </InputAdornment>
                        }
                      >
                        {jobPositions.map((position) => (
                          <MenuItem key={position.id} value={position.name}>
                            {position.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.job_position && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 1 }}>
                          {validationErrors.job_position}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Selector de rol */}
          {(selectedPerson || (modalTab === 1)) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Seleccionar rol:
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={selectedRole}
                  label="Rol"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <MenuItem value="client">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Cliente" color="info" size="small" />
                      <Typography variant="body2">
                        Acceso básico para consultar información de su expediente
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="lawyer">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Abogado" color="primary" size="small" />
                      <Typography variant="body2">
                        Profesional legal con acceso completo a expedientes
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="partner">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Colaborador" color="warning" size="small" />
                      <Typography variant="body2">
                        Puede editar y gestionar expedientes
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Administrador" color="error" size="small" />
                      <Typography variant="body2">
                        Control total del despacho
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 2
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
        >
          Cancelar
        </Button>
        
        {modalTab === 0 ? (
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={!selectedPerson || inviting}
            sx={{ 
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' }
            }}
          >
            {inviting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Invitando...
              </Box>
            ) : (
              'Invitar al despacho'
            )}
          </Button>
        ) : (
          <Button
            onClick={handleRegisterAndInvite}
            variant="contained"
            disabled={!newUser.name || !newUser.last_name || !newUser.email || !newUser.document_number || registering}
            sx={{ 
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            {registering ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Registrando...
              </Box>
            ) : (
              'Registrar e invitar'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteMemberModal;